import React from 'react';
import CopyToClipboardButton from '../../components/CopyToClipboardButton';
import { accessor, generateOrderCopiedText } from '../../helpers';
import { orderStatuses } from '../../names';
import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import OutsideClickHandler from 'react-outside-click-handler';

function isRevokableOrder(order) {
    const isModernStatus = order.orderStatus.name === orderStatuses.MODERN;
    const finished = order.details.finished;
    if (!isModernStatus && !finished && order.worker && !isDeleted(order)) {
        return true;
    }
    return false;
}
function isAssignableOrder(order) {
    return order.orderStatus.name === orderStatuses.NOT_ASSIGNED && !isDeleted(order);
}
function isDeletable(order) {
    return isAssignableOrder(order);
}
function isFinishableOrder(order) {
    const finished = order.details.finished;
    if (finished || isDeleted(order)) {
        return false;
    }
    return true;
}
function isFinished(order) {
    const isModernStatus = order.orderStatus.name === orderStatuses.MODERN;
    return !isDeleted(order) && (isModernStatus || order.details.finished);
}
function isDeleted(order) {
    return order.details.deleted;
}
function is90DaysLeft(order) {
    const _90DaysTimestamp = 1000 * 3600 * 24 * 90;
    return order.completionDate ? Date.now() - _90DaysTimestamp > order.completionDate : false;
}
function hasDeps(order) {
    const warranties = order.details.warranties || [];
    const repeats = order.details.repeats || [];
    if (!warranties.length && !repeats.length) return false;
    const deps = [...warranties, ...repeats];
    const root = order.details.root;
    if (deps.length === 1 && deps[0] === order._id && (root === order._id || root === null)) return false;
    return true;
}

const fieldSets = [
    {
        legend: 'Основное',
        options: [
            {
                label: 'Назначить',
                type: 'ASSIGN',
                access: (user) => accessor.assignOrder(user),
                visible: (order) => isAssignableOrder(order),
            },
            {
                label: 'Отозвать',
                type: 'REVOKE',
                access: (user) => accessor.revokeOrder(user),
                visible: (order) => isRevokableOrder(order),
            },
            {
                label: 'Закрыть заявку',
                type: 'FINISH',
                access: (user) => accessor.finishOrder(user),
                visible: (order) => isFinishableOrder(order),
            },
            {
                label: 'Сбросить',
                type: 'RESET',
                access: (user) => accessor.resetOrder(user),
                visible: (order) => isFinished(order),
            },
        ],
    },
    {
        legend: 'Редактирование',
        options: [
            {
                label: 'Редактировать',
                type: 'EDIT',
                access: (user) => accessor.editOrder(user),
                visible: (order) => !isDeleted(order),
            },
            {
                label: 'Удалить',
                type: 'DELETE',
                access: (user) => accessor.deleteOrder(user),
                visible: (order) => isDeletable(order),
            },
        ],
    },
    {
        legend: 'Зависимости',
        options: [
            {
                label: 'Создать гарантию',
                type: 'WARRANTY',
                access: (user) => accessor.createOrder(user),
                visible: (order) => !isDeleted(order) && !is90DaysLeft(order),
            },
            {
                label: 'Создать повтор',
                type: 'REPEAT',
                access: (user) => accessor.createOrder(user),
                visible: (order) => !isDeleted(order) && is90DaysLeft(order),
            },
            {
                label: 'Связанные заявки',
                type: 'DEPS',
                access: (_) => true,
                visible: (order) => hasDeps(order),
            },
        ],
    },
];

const MenuFieldset = ({ legend, funcs, options, order, user }) => {
    return (
        <fieldset>
            <legend>{legend}</legend>
            {options.map((o, index) =>
                o.access(user) && o.visible(order) ? (
                    
                    <div className="context-menu__option dark-theme-bg0-hover-bg1" key={index} onClick={funcs[o.type]}>
                        {o.label}
                    </div>
                ) : null,
            )}
        </fieldset>
    );
};

export const OrderContextMenu = ({ order, zoom, position, close, actions }) => {
    const posObj = { left: `${position.x/zoom}px`, top: `${position.y/zoom}px` };
    const user = useSelector((state) => state.auth);

    const setTextCopyHandler = () => {
        const hasPhoneAccess = accessor.clientPhone(user);
        const showClientPhone = hasPhoneAccess && !window.confirm('Скрыть номер клиента?');
        return generateOrderCopiedText(order, showClientPhone);
    }
    
    const funcs = {
        ASSIGN: actions.openWorkerPopup,
        WARRANTY: () => actions.openDepPopup(true),
        REPEAT: () => actions.openDepPopup(false),
        DEPS: actions.getDeps,
        EVENTS: actions.getEvents,
        REVOKE: actions.revokeOrder,
        RESET: actions.resetOrder,
        DELETE: actions.deleteOrder,
        FINISH: actions.openFinishPopup,
        EDIT: () => actions.setEditOrder(true),
    };
    const {setPhotoPopup} = actions;

    return createPortal(
        <OutsideClickHandler onOutsideClick={close}>
            <div className="context-menu dark-theme-bg0" style={posObj}>
                <div className="context-menu__title">Заявка id{order.id}</div>
                <div className="context-menu__body">
                    {fieldSets.map((f, index) => (
                        <MenuFieldset
                            funcs={funcs}
                            user={user}
                            order={order}
                            key={index}
                            legend={f.legend}
                            options={f.options}
                        />
                    ))}
                    <fieldset>
                        <legend>Другое</legend>
                        <CopyToClipboardButton
                            setTextToCopy={setTextCopyHandler}
                            className="context-menu__option"
                            closeMenu={close}
                        />
                        {accessor.orderHistory(user) && (
                            <div className="context-menu__option" onClick={() => funcs.EVENTS()}>История</div>
                        )}
                        {!!order.BSOPhotos.length && <div className="context-menu__option" onClick={() => setPhotoPopup('bso')}>Фото БСО</div>}
                        {!!order.chequePhotos.length && <div className="context-menu__option" onClick={() => setPhotoPopup('cheque')}>Фото чеков</div>}
                    </fieldset>
                </div>
            </div>
        </OutsideClickHandler>,
        document.getElementById('order-menu-root'),
    );
};
